<script setup>
import { ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import AiAgentsAPI from '../../../../../api/aiAgents';

const props = defineProps({
  agent: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const newMessage = ref('');
const messages = ref([]);
const isLoading = ref(false);
const chatContainer = ref(null);
const inputRef = ref(null);

const formatTime = date =>
  new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const scrollToBottom = () => {
  if (chatContainer.value) {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
  }
};

const focusInput = () => {
  inputRef.value?.focus();
};

const sendMessage = async () => {
  const text = newMessage.value.trim();
  if (!text || isLoading.value) return;

  messages.value.push({
    id: Date.now(),
    content: text,
    type: 'user',
    createdAt: new Date(),
  });
  newMessage.value = '';
  isLoading.value = true;
  await nextTick(scrollToBottom);

  try {
    const history = messages.value.map(msg => ({
      role: msg.type === 'user' ? 'user' : 'assistant',
      content: msg.content,
    }));
    const { data } = await AiAgentsAPI.playground(
      props.agent.id,
      text,
      history
    );
    messages.value.push({
      id: Date.now() + 1,
      content: data.response,
      type: 'agent',
      createdAt: new Date(),
    });
  } finally {
    isLoading.value = false;
    await nextTick();
    scrollToBottom();
    focusInput();
  }
};
</script>

<template>
  <div class="flex justify-center p-4">
    <div
      class="relative w-full max-w-[360px] h-[640px] rounded-[3rem] border-[14px] border-n-slate-8 bg-n-slate-8 shadow-2xl overflow-hidden"
    >
      <div
        class="absolute top-0 left-0 right-0 z-10 flex justify-center pt-2 pb-1"
      >
        <div
          class="w-20 h-5 bg-n-slate-8 rounded-b-2xl flex justify-center items-center gap-2"
        >
          <div class="w-1.5 h-1.5 rounded-full bg-n-slate-4" />
          <div class="w-8 h-1 rounded-full bg-n-slate-4" />
        </div>
      </div>

      <div class="flex flex-col h-full bg-n-slate-2 pt-8">
        <div
          class="flex items-center gap-3 px-4 py-3 border-b border-n-slate-6 bg-n-slate-2"
        >
          <div class="w-8 h-8 rounded-full bg-n-slate-6" />
          <div class="text-sm font-medium text-n-slate-12">
            {{ agent.name }}
          </div>
        </div>

        <div ref="chatContainer" class="flex-1 overflow-y-auto p-4 space-y-2">
          <div
            v-if="messages.length === 0 && !isLoading"
            class="h-full flex items-center justify-center text-sm text-n-slate-10 text-center px-4"
          >
            {{ t('AI_AGENTS.PLAYGROUND.PLACEHOLDER') }}
          </div>
          <template v-else>
            <div
              v-for="msg in messages"
              :key="msg.id"
              class="flex w-full"
              :class="msg.type === 'user' ? 'justify-end' : 'justify-start'"
            >
              <div
                class="max-w-[80%] px-4 py-2 text-sm shadow-sm whitespace-pre-wrap"
                :class="
                  msg.type === 'user'
                    ? 'rounded-2xl rounded-tr-sm bg-green-100 text-n-slate-12 dark:bg-green-900 dark:text-n-slate-1'
                    : 'rounded-2xl rounded-tl-sm bg-white border border-n-slate-5 text-n-slate-12 dark:bg-n-slate-4 dark:border-n-slate-6'
                "
              >
                {{ msg.content }}
                <div
                  class="mt-1 text-right text-xxs text-n-slate-10"
                  :class="
                    msg.type === 'user'
                      ? 'text-green-700 dark:text-green-200'
                      : ''
                  "
                >
                  {{ formatTime(msg.createdAt) }}
                </div>
              </div>
            </div>
            <div v-if="isLoading" class="flex w-full justify-start">
              <div
                class="max-w-[80%] px-4 py-2 text-sm rounded-2xl rounded-tl-sm bg-white border border-n-slate-5 text-n-slate-10 dark:bg-n-slate-4 dark:border-n-slate-6"
              >
                {{ t('AI_AGENTS.PLAYGROUND.LOADING') }}
              </div>
            </div>
          </template>
        </div>

        <div class="flex gap-2 p-3 border-t border-n-slate-6 bg-n-surface-1">
          <input
            ref="inputRef"
            v-model="newMessage"
            type="text"
            class="flex-1 px-4 py-2 text-sm rounded-full border border-n-slate-6 bg-n-surface-2 focus:outline-none focus:ring-2 focus:ring-n-brand"
            :placeholder="t('AI_AGENTS.PLAYGROUND.INPUT_PLACEHOLDER')"
            :disabled="isLoading"
            @keyup.enter="sendMessage"
          />
          <button
            class="px-4 py-2 text-sm font-medium rounded-full bg-n-brand text-white hover:bg-n-brand/90 disabled:opacity-50"
            :disabled="isLoading || !newMessage.trim()"
            @click="sendMessage"
          >
            {{ t('AI_AGENTS.PLAYGROUND.SEND') }}
          </button>
        </div>

        <div class="flex justify-center pb-2 pt-1 bg-n-surface-1">
          <div class="w-28 h-1 rounded-full bg-n-slate-6" />
        </div>
      </div>
    </div>
  </div>
</template>
